import { AxiosError } from "axios";
import { useState, useCallback } from "react";

import { ApiResponse } from "../interfaces/paginated";
import { Withdrawal } from "../interfaces/withdrawal";
import { api } from "../services/api";

type WithdrawalStatusAction = "approve" | "reject";

type UpdateWithdrawalStatusParams<T> = {
    onSuccess?: (updatedData: T | null) => void;
    onError?: (updatedData: T | null, message?: string) => void;
    onFinally?: () => void;
};

export function useUpdateWithdrawalStatus<T>({
    onSuccess,
    onError,
    onFinally,
}: UpdateWithdrawalStatusParams<T>) {
    const [isSubmitting, setIsSubmitting] = useState<
        WithdrawalStatusAction | false
    >(false);

    const mutate = useCallback(
        async (
            id: string,
            action: WithdrawalStatusAction,
            data?: Partial<Withdrawal>
        ) => {
            setIsSubmitting(action);

            try {
                const response = await api.patch<ApiResponse<null>>(
                    `/withdrawal/${action}/${id}`,
                    data
                );

                if (!response.data.status) {
                    onError?.(null, response.data.message);
                    return;
                }

                onSuccess?.(null);
            } catch (error: unknown) {
                const axiosError = error as AxiosError<ApiResponse<null>>;

                onError?.(
                    null,
                    axiosError?.response?.data?.message || axiosError?.message
                );
            } finally {
                setIsSubmitting(false);
                onFinally?.();
            }
        },
        [onSuccess, onError, onFinally]
    );

    return { isSubmitting, mutate };
}
